import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { KieneTableService } from '../../../components/kiene-table/kiene-table.service';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { KieneUtils } from '../../../../../projects/kiene-core/src/lib/utils/KieneUtils';
import { Invoice, InvoicePosition } from '../rechnung';
import {
    RechnungBelegeDialogComponent,
    StornoResult,
} from '../rechnung-belege-dialog/rechnung-belege-dialog.component';
import { RechnungStornoDialogComponent } from '../rechnung-storno-dialog/rechnung-storno-dialog.component';
import { HttpParams } from '@angular/common/http';
import {
    PdfViewerDesktopService,
    PdfViewerDialogInput,
} from '../../../../../projects/kiene-core/src/lib/components/pdf-viewer/kiene-pdf-viewer/kiene-pdf-viewer-desktop/pdf-viewer-desktop.service';

@Component({
    selector: 'kiene-rechnung-details',
    templateUrl: './rechnung-details.component.html',
    styleUrls: ['./rechnung-details.component.scss'],
})
export class RechnungDetailsComponent implements OnInit {
    rechnungId: string;
    rechnung: Invoice = new Invoice();
    rechnungsdatumCtrl = new UntypedFormControl();

    constructor(
        private readonly route: ActivatedRoute,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly api: KieneTableService,
        private readonly messageService: MessageService,
        private readonly pdfService: PdfViewerDesktopService,
        public dialog: MatDialog,
        private readonly router: Router,
    ) {
        this.rechnungId = this.route.snapshot.paramMap.get('id');
    }

    ngOnInit() {
        this.loadRechnung();
    }

    loadRechnung() {
        this.api
            .getOne(this.apiBaseUrl + 'rechnung/read_one.php', 'rechnung_id', this.rechnungId)
            .subscribe(response => {
                this.rechnung = response;
                this.setRechnungsdatum();
            });
    }

    setRechnungsdatum() {
        const rd = new Date(this.rechnung.rechnungsdatum);
        this.rechnungsdatumCtrl.setValue(rd);
    }

    goBack() {
        window.history.go(-1);
    }

    getRechnungssumme() {
        let gesamtpreis = 0;
        for (const p of this.rechnung.positionen) {
            gesamtpreis += p.einzelpreis * p.artikelmenge;
        }
        return gesamtpreis;
    }

    getMwst() {
        let mwst = 0;
        for (const p of this.rechnung.positionen) {
            const preis = p.einzelpreis * p.artikelmenge;
            mwst += (preis * p.mwstsatz) / 100;
        }
        return mwst;
    }

    getMwstSaetze() {
        const mwstSaetze = [];
        for (const p of this.rechnung.positionen) {
            let found = false;
            for (const m of mwstSaetze) {
                if (m.mwst === p.mwstsatz) {
                    m.betrag += (p.einzelpreis * p.artikelmenge * p.mwstsatz) / 100;
                    found = true;
                }
            }
            if (!found) {
                const mwstSatz = { mwst: p.mwstsatz, betrag: (p.einzelpreis * p.artikelmenge * p.mwstsatz) / 100 };
                mwstSaetze.push(mwstSatz);
            }
        }
        return mwstSaetze;
    }

    isInvoiceEditable() {
        return this.hasRechnungStatus(51);
    }

    hasRechnungStatus(...statusIds: number[]): boolean {
        return statusIds.some(id => KieneUtils.sameId(this.rechnung?.status_id, id));
    }

    getRechungsbetrag() {
        return this.getRechnungssumme() + this.getMwst();
    }

    showDocument(p: InvoicePosition) {
        if (p.abgabebeleg_id) {
            this.router.navigate(['/belege', 'abgabebelege', p.abgabebeleg_id]);
        } else if (p.befund_id) {
            this.router.navigate(['/labor', 'befundmitteilungen', p.befund_id]);
        }
    }

    getTyp(p: InvoicePosition) {
        if (p.abgabe > 0) {
            return 'Abgabe';
        }
        if (p.behandlung > 0) {
            return 'Beh.';
        }
        return '/';
    }

    einzelnFreigeben() {
        this.api.post(this.apiBaseUrl + 'rechnung/freigeben_einzeln.php', this.rechnung, null).subscribe(
            () => {
                this.messageService.infoMessage('Rechnung erfolgreich freigegeben!');
                this.loadRechnung();
            },
            error => {
                this.messageService.errorMessage('Rechnung konnte nicht freigegeben werden!', error);
            },
        );
    }

    openPdf() {
        const input = new PdfViewerDialogInput(
            'rechnung/read_or_mail_pdf.php',
            new HttpParams().set('rechnung_id', this.rechnung?.rechnung_id?.toString()),
            'Rechnung ' + this.rechnung?.rechnung_nr,
            { email: { sende_mail: true, kunde_id: this.rechnung?.kunde_id } },
        );
        input.isInvoice = true;
        this.pdfService.openPdfViewer(input).subscribe({
            next: () => {
                this.loadRechnung();
            },
        });
    }

    rechnungLoeschen() {
        this.api.deleteEntity(this.apiBaseUrl + 'rechnung/delete.php', this.rechnung).subscribe(
            () => {
                this.messageService.infoMessage('Rechnung wurde erfolgreich gelöscht!');
                this.router.navigate(['/finanzen', 'rechnungen']);
            },
            error => {
                this.messageService.errorMessage('Rechnung konnte nicht gelöscht werden!', error);
            },
        );
    }

    openStornoDialog() {
        this.dialog
            .open(RechnungStornoDialogComponent, { data: { invoice: this.rechnung } })
            .afterClosed()
            .subscribe({
                next: (result: Invoice) => {
                    if (result) {
                        this.messageService.infoMessage('Rechnung wurde storniert!');
                        this.loadRechnung();

                        const params = new HttpParams().set('rechnung_id', this.rechnung?.rechnung_id);
                        this.api.getOneByParams(this.apiBaseUrl + 'rechnung/read_storno.php', params).subscribe({
                            next: (response: StornoResult) => {
                                if (response?.abgabebelege?.length > 0 || response?.befunde?.length > 0) {
                                    this.openRechnungBelegeDialog(this.rechnung);
                                }
                            },
                        });
                    }
                },
            });
    }

    openRechnungBelegeDialog(invoice: Invoice) {
        this.dialog.open(RechnungBelegeDialogComponent, { width: '80%', data: { invoice: invoice } });
    }
}
